import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart,
  Legend,
} from "recharts";
import { Bolt } from "@mui/icons-material";
import moment from "moment";

// Couleurs par phase
const PHASE_COLORS = {
  l1: "#FF6B6B", // Rouge
  l2: "#4ECDC4", // Teal
  l3: "#FFD93D", // Jaune
};

/**
 * TriphaseGraph - Graphe barres groupées pour afficher la puissance (W) des 3 phases L1/L2/L3.
 *
 * Props:
 *   phaseHistories: [arrayL1, arrayL2, arrayL3] - historiques par phase
 *   fieldName: string - nom du champ à afficher (default: "power")
 *   groupedBy: "hourly" | "daily"
 *   aspect: number - ratio largeur/hauteur du graphe
 *   onBarClick: function - callback au clic sur une barre
 */
export default function TriphaseGraph({
  phaseHistories = [[], [], []],
  fieldName = "power",
  groupedBy = "hourly",
  aspect = 3,
  onBarClick,
}) {
  const isMobile = window.innerWidth < 900;
  const isLightMode =
    document.documentElement.getAttribute("data-theme") === "light";

  // Mode d'affichage : puissance (W) ou intensité (A)
  const [mode, setMode] = React.useState(
    fieldName === "current" ? "current" : "power"
  );
  const activeField = mode === "current" ? "current" : "power";
  const unit = mode === "current" ? "A" : "W";
  const title =
    mode === "current" ? "Intensité par phase (A)" : "Puissance par phase (W)";
  // Arrondi : 3 décimales pour les ampères, 2 pour les watts
  const roundValue = (v) =>
    mode === "current"
      ? Math.round((v || 0) * 1000) / 1000
      : Math.round((v || 0) * 100) / 100;

  // Fusionner les 3 historiques par timestamp
  const mergedData = React.useMemo(() => {
    const timeMap = {};

    ["l1", "l2", "l3"].forEach((phase, idx) => {
      const history = phaseHistories[idx] || [];
      history.forEach((entry) => {
        const key = entry.time_created;
        if (!timeMap[key]) {
          timeMap[key] = { time_created: key };
        }
        timeMap[key][`val_${phase}`] = roundValue(entry[activeField]);
      });
    });

    return Object.values(timeMap).sort(
      (a, b) => new Date(a.time_created) - new Date(b.time_created)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseHistories, activeField]);

  const formatTime = (time) => {
    if (groupedBy === "daily") return moment(time).format("DD/MM");
    return moment(time).format("HH:mm");
  };

  // Tooltip personnalisé
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const time = moment(label).format(
        groupedBy === "daily" ? "DD/MM/YYYY" : "HH:mm"
      );
      return (
        <Box
          sx={{
            backgroundColor: isLightMode
              ? "rgba(255, 255, 255, 0.98)"
              : "rgba(15, 20, 25, 0.95)",
            backdropFilter: "blur(10px)",
            p: 2,
            borderRadius: 3,
            border: `1px solid ${isLightMode ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.1)"}`,
            boxShadow: isLightMode
              ? "0 4px 16px rgba(0,0,0,0.15)"
              : "0 8px 32px rgba(0,0,0,0.4)",
            minWidth: 160,
          }}
        >
          <Typography
            sx={{
              color: isLightMode
                ? "#5a6b5a"
                : "rgba(255,255,255,0.6)",
              fontSize: "11px",
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {time}
          </Typography>
          {payload.map((entry, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 0.3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: entry.color,
                  }}
                />
                <Typography
                  sx={{
                    color: isLightMode
                      ? "#3d4a3d"
                      : "rgba(255,255,255,0.7)",
                    fontSize: "13px",
                  }}
                >
                  {entry.name}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: entry.color,
                  fontSize: "15px",
                  fontWeight: "bold",
                }}
              >
                {entry.value} {unit}
              </Typography>
            </Box>
          ))}
          {/* Total */}
          <Box
            sx={{
              borderTop: `1px solid ${isLightMode ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`,
              mt: 0.5,
              pt: 0.5,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                color: isLightMode
                  ? "#3d4a3d"
                  : "rgba(255,255,255,0.7)",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              Total
            </Typography>
            <Typography
              sx={{
                color: "#ffd54f",
                fontSize: "15px",
                fontWeight: "bold",
              }}
            >
              {payload.reduce((sum, e) => sum + (e.value || 0), 0).toFixed(mode === "current" ? 2 : 1)} {unit}
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card
      sx={{
        position: "relative",
        backgroundColor: isLightMode
          ? "#ffffff"
          : "rgba(25, 32, 28, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "12px !important",
        border: isLightMode
          ? "1px solid rgba(0,0,0,0.12)"
          : "1px solid rgba(255,255,255,0.06)",
        borderTop: "3px solid #ffd54f",
        boxShadow: isLightMode
          ? "0 2px 12px rgba(0,0,0,0.1)"
          : "0 4px 24px rgba(0,0,0,0.4)",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: isLightMode
            ? "0 4px 20px rgba(0,0,0,0.15), 0 0 10px #ffd54f20"
            : "0 8px 32px rgba(0,0,0,0.5), 0 0 20px #ffd54f15",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Bolt sx={{ fontSize: 24, color: "#ffd54f", opacity: 0.8 }} />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: isLightMode
                ? "#1a1f1a"
                : "rgba(255,255,255,0.9)",
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Toggle Watts / Ampères */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(e, next) => {
            if (next !== null) setMode(next);
          }}
          sx={{
            "& .MuiToggleButton-root": {
              px: 1.2,
              py: 0.2,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
              border: `1px solid ${isLightMode ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.15)"}`,
              color: isLightMode ? "#5a6b5a" : "rgba(255,255,255,0.6)",
              "&.Mui-selected": {
                backgroundColor: "#ffd54f",
                color: "#1a1f1a",
                "&:hover": { backgroundColor: "#ffcc33" },
              },
            },
          }}
        >
          <ToggleButton value="power">W</ToggleButton>
          <ToggleButton value="current">A</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <ResponsiveContainer aspect={aspect} width="100%">
          <BarChart
            data={mergedData}
            margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
            onClick={(state) => {
              if (onBarClick && state?.activePayload?.length > 0) {
                onBarClick(state.activePayload[0].payload);
              }
            }}
            style={{ cursor: onBarClick ? "pointer" : "default" }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={
                isLightMode
                  ? "rgba(0,0,0,0.1)"
                  : "rgba(255,255,255,0.06)"
              }
              vertical={false}
            />
            <XAxis
              dataKey="time_created"
              type="category"
              tickFormatter={formatTime}
              tick={{
                fontSize: isMobile ? 9 : 11,
                fill: isLightMode
                  ? "#3d4a3d"
                  : "rgba(255,255,255,0.5)",
              }}
              interval={isMobile ? 3 : 1}
              axisLine={{
                stroke: isLightMode
                  ? "rgba(0,0,0,0.15)"
                  : "rgba(255,255,255,0.08)",
              }}
              tickLine={false}
            />
            <YAxis
              tick={{
                fontSize: isMobile ? 9 : 11,
                fill: isLightMode
                  ? "#3d4a3d"
                  : "rgba(255,255,255,0.5)",
              }}
              axisLine={false}
              tickLine={false}
              width={55}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                fill: isLightMode
                  ? "rgba(0,0,0,0.04)"
                  : "rgba(255,255,255,0.03)",
                radius: 4,
              }}
            />
            <Legend
              wrapperStyle={{
                fontSize: isMobile ? 11 : 13,
                color: isLightMode ? "#3d4a3d" : "rgba(255,255,255,0.7)",
              }}
            />
            <Bar
              dataKey="val_l1"
              name="Phase L1"
              fill={PHASE_COLORS.l1}
              radius={[4, 4, 0, 0]}
              isAnimationActive={true}
              animationDuration={800}
            />
            <Bar
              dataKey="val_l2"
              name="Phase L2"
              fill={PHASE_COLORS.l2}
              radius={[4, 4, 0, 0]}
              isAnimationActive={true}
              animationDuration={800}
            />
            <Bar
              dataKey="val_l3"
              name="Phase L3"
              fill={PHASE_COLORS.l3}
              radius={[4, 4, 0, 0]}
              isAnimationActive={true}
              animationDuration={800}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
